/**
 * StateScopeEditor — the SHARED master/detail shell for a typed declared
 * **state bag**, used by BOTH `StateEditor` (`$page`) and `GlobalStateEditor`
 * (`$global`). The two scopes are the SAME shape (named entries · optional type ·
 * JSON initial) authored the SAME way; only the keying (page vars by `id`, app
 * globals by index), the persistence home, and a couple of page-only extras
 * (the "from components" promote list + the usage badge) differ. Those land via
 * thin per-scope WRAPPERS that adapt their data to `ScopeEntry` and inject the
 * extras through slots — so there is ONE editor shell, not two near-clones
 * (ADR-0048 single-IDE state-scope unification).
 *
 * Layout: master/detail like Query (WS-8) — a `view` prop (`list`|`detail`|`full`)
 * + CONTROLLED selection (by stable string key) so the two mounts agree; omit the
 * controlled props for the legacy single pane (back-compat for the unit tests).
 */
import { useEffect, useState, type ReactNode } from "react";
import { Plus, type LucideIcon } from "lucide-react";

import type { StateValueType } from "@/lib/pageTypes";

import { JsonValueInput } from "./JsonValueInput";

const TYPES: StateValueType[] = ["string", "number", "boolean", "object", "array", "any"];

/** A normalized state entry — the per-scope wrapper maps its own model (e.g.
 *  `PageVariable` keyed by `id`, `AppGlobalStateEntry` keyed by index) onto this. */
export interface ScopeEntry {
  /** Stable selection key (a page var's `id`, a global's stringified index). */
  key: string;
  name: string;
  type?: StateValueType | null;
  initial?: unknown;
}

type AreaView = "list" | "detail" | "full";

export function StateScopeEditor({
  entries,
  icon: Icon,
  listTitle,
  addLabel,
  emptyListLabel,
  nameAriaLabel,
  deleteTitle,
  deleteLabel,
  emptyDetailHint,
  showType = false,
  saving = false,
  nameError,
  listExtra,
  detailFooter,
  view = "full",
  selectedKey,
  onSelectKey,
  onAdd,
  onPatch,
  onRemove,
}: {
  entries: ScopeEntry[];
  icon: LucideIcon;
  listTitle: string;
  addLabel: string;
  emptyListLabel: string;
  /** aria-label for the name input (scope-specific so the tests can target it). */
  nameAriaLabel: string;
  deleteTitle: string;
  deleteLabel: string;
  /** Shown in the detail when nothing is selected (the scope's lifecycle hint). */
  emptyDetailHint: ReactNode;
  /** Expose the type `<select>` (both scopes are typed bags). */
  showType?: boolean;
  /** Disable mutating controls while an async save is in flight (global). */
  saving?: boolean;
  /** Per-entry name validity → a danger border + tooltip on the name input. */
  nameError?: (entry: ScopeEntry) => { dupe?: boolean; bad?: boolean } | undefined;
  /** Extra master content (e.g. the page's "from components" promote list). The
   *  `select` helper lets it move the selection (used after promoting a key). */
  listExtra?: (helpers: { select: (key: string) => void }) => ReactNode;
  /** Extra detail footer (e.g. the page's usage badge). */
  detailFooter?: (entry: ScopeEntry) => ReactNode;
  view?: AreaView;
  /** Controlled selection (master/detail split). Omit for the legacy single pane. */
  selectedKey?: string | null;
  onSelectKey?: (key: string | null) => void;
  /** Append an entry; returns the new entry's key so the shell selects it. */
  onAdd: () => string | undefined;
  onPatch: (key: string, next: Partial<ScopeEntry>) => void;
  onRemove: (entry: ScopeEntry) => void;
}) {
  const controlled = onSelectKey !== undefined;
  const [internalKey, setInternalKey] = useState<string | null>(null);
  const curKey = controlled ? (selectedKey ?? null) : internalKey;
  const setSel = (k: string | null) => (controlled ? onSelectKey!(k) : setInternalKey(k));

  // The entry open in the detail pane (falls back to the first).
  const selected = entries.find((e) => e.key === curKey) ?? entries[0] ?? null;
  // Drop a stale internal selection when its entry is gone.
  useEffect(() => {
    if (!controlled && internalKey && !entries.some((e) => e.key === internalKey))
      setInternalKey(null);
  }, [entries, internalKey, controlled]);

  const handleAdd = () => {
    const k = onAdd();
    if (k) setSel(k);
  };

  const err = selected && nameError ? nameError(selected) : undefined;
  const flagged = !!(err?.dupe || err?.bad);

  const masterCls =
    view === "full"
      ? "flex w-56 shrink-0 flex-col gap-1 overflow-y-auto border-r border-border/60 pr-2"
      : "flex h-full flex-col gap-1 overflow-y-auto text-[12px]";

  const master = (
    <div className={masterCls}>
      <div className="mb-2 px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {listTitle}
      </div>
      <button
        type="button"
        onClick={handleAdd}
        disabled={saving}
        className="mb-1 flex items-center justify-center gap-1.5 rounded-md border border-dashed border-border px-2 py-1.5 text-xs text-muted-foreground hover:border-accent/60 hover:text-foreground disabled:opacity-40"
      >
        <Plus className="h-3.5 w-3.5" strokeWidth={1.5} /> {addLabel}
      </button>
      {entries.length === 0 ? (
        <p className="px-1 py-1 text-[11px] text-muted-foreground">{emptyListLabel}</p>
      ) : (
        entries.map((e) => {
          const active = selected?.key === e.key;
          return (
            <button
              key={e.key}
              type="button"
              onClick={() => setSel(e.key)}
              className={`flex items-center gap-1.5 rounded-md px-2 py-1.5 text-left ${
                active ? "bg-accent/15 text-accent" : "hover:bg-surface"
              }`}
            >
              <Icon className="h-3.5 w-3.5 shrink-0" />
              <span className="min-w-0 flex-1 truncate font-mono">{e.name}</span>
            </button>
          );
        })
      )}
      {listExtra?.({ select: setSel })}
    </div>
  );

  const detailInner = !selected ? (
    <div className="flex h-full flex-col items-center justify-center gap-2 text-center text-[11px] text-muted-foreground">
      {emptyDetailHint}
    </div>
  ) : (
    <div className="flex max-w-xl flex-col gap-3">
      <div className="flex flex-col gap-1">
        <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
          Nome
        </label>
        <input
          aria-label={nameAriaLabel}
          className={`w-64 rounded border bg-background px-2 py-1.5 font-mono ${
            flagged ? "border-danger" : "border-border"
          }`}
          value={selected.name}
          title={err?.dupe ? "Nome duplicato" : err?.bad ? "Identificatore non valido (no $)" : undefined}
          onChange={(ev) => onPatch(selected.key, { name: ev.target.value })}
        />
      </div>
      {showType && (
        <div className="flex flex-col gap-1">
          <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
            Tipo
          </label>
          <select
            aria-label="Tipo"
            className="w-40 rounded border border-border bg-background px-1.5 py-1.5 text-[11px]"
            value={selected.type ?? ""}
            onChange={(ev) =>
              onPatch(selected.key, { type: ev.target.value ? (ev.target.value as StateValueType) : null })
            }
          >
            <option value="">(tipo)</option>
            {TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
      )}
      <div className="flex flex-col gap-1">
        <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
          Valore iniziale (JSON)
        </label>
        <JsonValueInput
          aria-label="Valore iniziale"
          className="w-full rounded border border-border bg-background px-2 py-1.5 font-mono text-[11px]"
          placeholder='valore iniziale (JSON) — es. 0, "tab1", false'
          value={selected.initial}
          onChange={(val) => onPatch(selected.key, { initial: val })}
        />
      </div>
      <div className="flex items-center gap-3">
        {detailFooter?.(selected)}
        <button
          type="button"
          className="text-[11px] text-danger hover:underline"
          title={deleteTitle}
          onClick={() => onRemove(selected)}
        >
          {deleteLabel}
        </button>
      </div>
    </div>
  );

  if (view === "list") return master;
  if (view === "detail")
    return <div className="h-full overflow-y-auto text-[12px]">{detailInner}</div>;

  return (
    <div className="flex h-full gap-3 text-[12px]">
      {master}
      <div className="min-h-0 min-w-0 flex-1 overflow-y-auto">{detailInner}</div>
    </div>
  );
}
